import { useMemo } from "react";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { EntityItemConfig, EntityPoolConfig } from "@salestrak/ui";
import { compareStages } from "#app/lib/stage-order";
import { stageTypeLabel } from "#app/lib/entity-visuals";
import { poolItemVisual } from "../utils";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "stages";

export type StagePoolStage = Omittable<{
  stageId: string;
  name: string;
  type?: string | null;
  icon?: { color?: string | null; percentage?: number | null } | null;
  sortOrder?: number | null;
  isDefault?: boolean | null;
}>;

// THE stage → selector-item mapping. Every selector/filter that lists stages
// goes through this so the glyph treatment can't drift per call site.
export function stageToPoolItem(
  node: StagePoolStage,
  description?: string,
): EntityItemConfig {
  const stage = {
    type: node.type ?? null,
    icon: {
      color: node.icon?.color ?? null,
      percentage: node.icon?.percentage ?? 0,
    },
  };
  return {
    id: node.stageId,
    title: node.name,
    description,
    avatar: {
      render: () =>
        poolItemVisual({ kind: "stage", id: node.stageId, stage }),
    },
    data: {
      type: node.type,
      sortOrder: node.sortOrder,
      isDefault: node.isDefault,
      icon: stage.icon,
    },
  } satisfies EntityItemConfig;
}

/** Builds one workflow's Stage pool from the workspace snapshot. */
export function useStagePool(workflowId: string | null | undefined) {
  const { isLoading: loading } = useAppData();
  const { stagesByWorkflow } = useWorkspaceData();

  const items = useMemo<EntityItemConfig[]>(() => {
    if (!workflowId) return [];
    // Sorted by category then sortOrder, so each category is ONE contiguous
    // run and `section` draws one group heading per category that has a stage —
    // an empty category draws nothing. The mapper itself stays section-free
    // because the cross-workflow filter list it also feeds is not sorted by
    // category and would interleave headings.
    return (stagesByWorkflow.get(workflowId) ?? [])
      .map((stage) => ({
        ...stageToPoolItem(stage),
        section: stageTypeLabel(stage.type) ?? undefined,
      }))
      .sort((a, b) =>
        compareStages(
          { type: a.data?.type as string | null, sortOrder: a.data?.sortOrder as number | null },
          { type: b.data?.type as string | null, sortOrder: b.data?.sortOrder as number | null },
        ),
      );
  }, [stagesByWorkflow, workflowId]);

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Stages",
      items,
      // Categories read as sentence-case labels with a rule between runs
      // (the stage popover's grammar), never the caps eyebrow.
      sectionHeadings: "label",
      sectionRules: true,
    }),
    [items],
  );

  return { pool, loading };
}

export { POOL_ID as STAGE_POOL_ID };
